import { RadioGroup as RadioGroupPrimitive } from "radix-ui";
import { useId } from "react";
import type * as React from "react";
import { cn } from "@/lib/utils";

/** A radio group drawn as cards, as in GitHub's "new repository" form; the checked card takes the focus color. */
function ChoiceGroup<T extends string>({
  legend,
  value,
  onValueChange,
  className,
  children,
}: {
  legend: string;
  value: T;
  onValueChange: (v: T) => void;
  className?: string;
  children: React.ReactNode;
}) {
  const id = useId();
  return (
    <div className="min-w-0">
      <div id={id} className="mb-2 font-semibold">
        {legend}
      </div>
      <RadioGroupPrimitive.Root
        aria-labelledby={id}
        value={value}
        onValueChange={(v) => onValueChange(v as T)}
        className={cn("grid grid-cols-[repeat(auto-fit,minmax(190px,1fr))] gap-2", className)}
      >
        {children}
      </RadioGroupPrimitive.Root>
    </div>
  );
}

function ChoiceCard({
  value,
  title,
  description,
  disabled,
  children,
}: {
  value: string;
  title: React.ReactNode;
  description?: React.ReactNode;
  disabled?: boolean;
  children?: React.ReactNode;
}) {
  return (
    <label
      className={cn(
        "flex cursor-pointer items-start gap-2 rounded-md border p-3 hover:bg-muted",
        "has-data-[state=checked]:border-ring has-data-[state=checked]:bg-info-subtle has-data-[state=checked]:[&_[data-slot=choice-title]_svg]:text-info",
        "has-focus-visible:outline-2 has-focus-visible:-outline-offset-2 has-focus-visible:outline-ring",
        disabled && "cursor-not-allowed opacity-60 hover:bg-transparent",
      )}
    >
      <RadioGroupPrimitive.Item
        value={value}
        disabled={disabled}
        className="mt-[3px] size-4 shrink-0 rounded-full border border-input bg-background focus-visible:outline-none data-[state=checked]:border-[5px] data-[state=checked]:border-ring"
      />
      <span className="flex min-w-0 flex-1 flex-col gap-0.5">
        <span data-slot="choice-title" className="flex items-center gap-1.5 font-semibold leading-5 [&_svg]:shrink-0 [&_svg]:text-muted-foreground">
          {title}
        </span>
        {description && <span className="text-xs text-muted-foreground">{description}</span>}
        {children}
      </span>
    </label>
  );
}

export { ChoiceCard, ChoiceGroup };
